import { CollectionConfig } from "@rebasepro/types";

/**
 * Email Messages — what was actually said, in both directions.
 *
 * One table for sent and received, because a conversation split across two
 * tables is not a conversation. `direction` separates them and `status` says
 * what happened to each.
 *
 * ── What the statuses claim ──────────────────────────────────────────
 *
 *   Recibido  — it arrived. The only status an inbound message can have.
 *   Enviado   — the SMTP server accepted it. **Not** delivered and **not** read:
 *               nothing in this product can observe either, so no value names
 *               them. This is the same rule `prospect_contacts.email_status`
 *               follows in refusing a `verified`.
 *   Falló     — SMTP refused it or the connection died. The row is written
 *               anyway and keeps its text, so the attempt is visible and the
 *               draft is not lost.
 *
 * There is no `draft`. A draft lives in the composer until somebody sends it;
 * a status nothing ever writes is a promise the code does not keep.
 *
 * ── About the bodies ─────────────────────────────────────────────────
 *
 * `body_html` is stored **already sanitized** — `services/email-sanitize.ts`
 * runs on the way in, so the dangerous form of a stranger's markup is never at
 * rest in this database. The reader renders it inside a sandboxed iframe on top
 * of that, because one layer is how these stories start (33e0a3b8 was a stored
 * `javascript:` URL from a far friendlier source than an email from anyone).
 *
 * Attachments are recorded by name and never stored. We have no retention policy
 * for a stranger's PDF and no reason to hold one; `attachment_names` exists so
 * the thread does not silently lose the fact that something came with it.
 *
 * Admin-only and FORCE RLS (drizzle/0042) — see `email_threads`.
 */
const emailMessagesCollection: CollectionConfig = {
    name: "Mensajes",
    singularName: "Mensaje",
    slug: "email-messages",
    table: "email_messages",
    securityRules: [
        {
            name: "admin_full_access",
            operation: "all",
            roles: ["admin"]
        }
    ],
    properties: {
        id: {
            name: "ID",
            type: "string",
            isId: "uuid"
        },
        thread_id: {
            name: "Conversación",
            type: "string",
            validation: { required: true },
            admin: { readOnly: true }
        },
        direction: {
            name: "Dirección",
            type: "string",
            enum: [
                { id: "inbound", label: "Recibido", color: "green" },
                { id: "outbound", label: "Enviado", color: "blue" }
            ],
            validation: { required: true }
        },
        status: {
            name: "Estado",
            type: "string",
            enum: [
                { id: "received", label: "Recibido", color: "green" },
                { id: "sent", label: "Enviado", color: "blue" },
                { id: "failed", label: "Falló", color: "red" }
                // No "entregado" ni "leído": nada aquí puede saberlo.
            ],
            validation: { required: true }
        },
        message_id: {
            name: "Message-ID",
            type: "string",
            description: "El identificador RFC 5322. Es único en la base de datos: es lo que hace que volver a leer el buzón no duplique nada.",
            admin: { readOnly: true }
        },
        in_reply_to: {
            name: "Responde a",
            type: "string",
            admin: { readOnly: true }
        },
        refs: {
            name: "Referencias",
            type: "array",
            columnType: "text[]",
            of: { type: "string", name: "Message-ID" },
            admin: { readOnly: true }
        },
        from_email: {
            name: "De",
            type: "string",
            validation: { required: true }
        },
        from_name: {
            name: "Nombre del remitente",
            type: "string"
        },
        to_emails: {
            name: "Para",
            type: "array",
            columnType: "text[]",
            of: { type: "string", name: "Dirección" }
        },
        cc_emails: {
            name: "Copia",
            type: "array",
            columnType: "text[]",
            of: { type: "string", name: "Dirección" }
        },
        subject: {
            name: "Asunto",
            type: "string"
        },
        body_text: {
            name: "Texto",
            type: "string",
            admin: { multiline: true }
        },
        body_html: {
            name: "HTML",
            type: "string",
            description: "Ya saneado por services/email-sanitize.ts al entrar. Aun así se muestra dentro de un iframe aislado.",
            admin: { multiline: true, readOnly: true }
        },
        body_snippet: {
            name: "Resumen",
            type: "string",
            description: "El mensaje sin la cadena de respuestas citada. Es lo que se lee primero.",
            admin: { readOnly: true }
        },
        has_attachments: {
            name: "Con adjuntos",
            type: "boolean",
            defaultValue: false
        },
        attachment_names: {
            name: "Adjuntos",
            type: "array",
            columnType: "text[]",
            of: { type: "string", name: "Nombre" },
            description: "Sólo los nombres. Los archivos no se guardan aquí: están en el buzón.",
            admin: { readOnly: true }
        },
        sent_at: {
            name: "Fecha",
            type: "date",
            description: "La cabecera Date en lo recibido; el momento en que SMTP lo aceptó en lo enviado."
        },
        actor: {
            name: "Quién lo envió",
            type: "string",
            admin: { readOnly: true }
        },
        error: {
            name: "Error",
            type: "string",
            admin: { readOnly: true }
        },
        imap_mailbox: {
            name: "Buzón IMAP",
            type: "string",
            admin: { readOnly: true }
        },
        imap_uid: {
            name: "UID IMAP",
            type: "number",
            admin: { readOnly: true }
        },
        gmail_thread_id: {
            name: "Conversación en Gmail",
            type: "string",
            admin: { readOnly: true }
        },
        gmail_message_id: {
            name: "Mensaje en Gmail",
            type: "string",
            description: "X-GM-MSGID. Su forma hexadecimal es la URL del mensaje en Gmail.",
            admin: { readOnly: true }
        },
        created_at: {
            name: "Creado",
            type: "date",
            autoValue: "on_create"
        }
    },
    admin: {
        group: "Correo",
        icon: "MailOpen",
        sort: ["sent_at", "desc"],
        // Fuera del menú: esta tabla es el interior de "Correo",
        // no una pantalla. Como entrada del menú era tres cosas confusas a la
        // vez — vacía hasta que alguien actúa, sin explicar qué es, y un
        // segundo camino peor hacia "Correo".
        //
        // `group` por sí solo NO la ocultaba: el menú muestra cualquier
        // colección que declare uno, esté o no en `navigationGroupMappings`.
        // Sigue siendo navegable — el lápiz de la pantalla abre la fila.
        hideFromNavigation: true
    }
};

export default emailMessagesCollection;
